import { scaleLinear } from 'd3-scale';
import {
  type Attributes,
  type PropsWithChildren,
  forwardRef,
  useState,
} from 'react';

import { GanttChartLineRenderer } from './components/GanttChartLineRenderer.js';
import { GanttChartTooltip } from './components/tooltip/GanttChartTooltip.js';
import {
  DEFAULT_GANTT_LINE_SIZE,
  GANTT_CHART_V2_ARIA_LABEL,
  GANTT_LINE_SIZE_HEIGHT,
} from './constants/defaults.js';
import { GanttSegmentsContext } from './context/GanttSegmentsContext.js';
import { wrapperCSS } from './GanttChartLine.sty.js';
import {
  useTooltipPinnedStatus,
  useTooltipVisibility,
} from './hooks/use-set-tooltip-state.js';
import { GanttSegmentData } from './types/gantt-chart.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import type { DataTableV2GanttColumnConfig } from '../../tables/DataTableV2/features/ColumnTypes/ColumnTypes.js';
import { useObserveContainerSize } from '../core/hooks/useObserveContainerSize.js';
import { EmptyStateSlot } from '../core/slots/chart-states/EmptyStateSlot.js';
import { ErrorStateSlot } from '../core/slots/chart-states/ErrorStateSlot.js';

type GanttChartLineProps = Attributes & {
  min: number;
  max: number;
  size?: 'default' | 'condensed' | 'comfortable';
  /** When true: Sets an overlay with default loader. */
  loading?: boolean;
  segmentsData: GanttSegmentData[];
  parentSegmentsData?: GanttSegmentData[];
  config: DataTableV2GanttColumnConfig;
};

/** GanttChartLine component */
const _GanttChartLine = forwardRef<
  HTMLDivElement,
  PropsWithChildren<GanttChartLineProps>
>((props, forwardedRef) => {
  const [mouseInside, setMouseInside] = useState(false);
  const isTooltipVisible = useTooltipVisibility();
  const isTooltipPinned = useTooltipPinnedStatus();

  const { size, segmentsData, parentSegmentsData, config, loading } = props;
  const [{ width }, containerRef] = useObserveContainerSize();
  const ref = useMergeRefs([forwardedRef, containerRef]);
  const height = GANTT_LINE_SIZE_HEIGHT[size ?? DEFAULT_GANTT_LINE_SIZE];

  const segmentScale = scaleLinear()
    .domain([props.min, props.max])
    .range([0, width]);

  return (
    <div
      className={wrapperCSS}
      onMouseEnter={() => !isTooltipPinned && setMouseInside(true)}
      onMouseLeave={() => !isTooltipPinned && setMouseInside(false)}
      role="img"
      aria-label={GANTT_CHART_V2_ARIA_LABEL}
    >
      <GanttSegmentsContext.Provider
        value={{
          segmentsData,
          parentSegmentsData,
          segmentScale,
          config,
          hovering: mouseInside,
        }}
      >
        <GanttChartLineRenderer
          width={width}
          height={height}
          loading={loading}
          ref={ref}
          showBackground={config.showBackground}
        />
        {isTooltipVisible && <GanttChartTooltip />}
      </GanttSegmentsContext.Provider>
    </div>
  );
});

/**
 * @internal
 */
export const GanttChartLine = Object.assign(_GanttChartLine, {
  EmptyState: EmptyStateSlot,
  ErrorState: ErrorStateSlot,
});
